import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import Header from './common/Header';
import Footer from './common/Footer';

const ContactPage = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    category: 'general',
    message: '',
    agreeToPrivacy: false,
  });

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Initialize EmailJS with your public key
      emailjs.init('1YES4s61T8zyFqTd_');

      // Prepare template parameters
      const templateParams = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        subject: formData.subject,
        category: formData.category,
        message: formData.message,
        timestamp: new Date().toLocaleString(),
      };

      // Send email using EmailJS
      const result = await emailjs.send(
        'service_y8i43dv', // Service ID
        'template_u0o0co7', // Template ID
        templateParams,
      );

      setFormSubmitted(true);
    } catch (error) {
      console.error('Error sending email:', error);
      // You could add error state handling here
      alert(
        'Sorry, there was an error sending your message. Please try again or email us directly at support@chillfree.tv',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (formSubmitted) {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <Header />

        <main className="container mx-auto px-4 py-12 max-w-4xl">
          <div className="text-center">
            <div className="w-24 h-24 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">✅</span>
            </div>
            <h1 className="text-4xl font-bold mb-4 text-green-400">Message Sent Successfully!</h1>
            <p className="text-gray-300 mb-8 leading-relaxed max-w-2xl mx-auto">
              Thank you for contacting ChillFree TV! We've received your message and will get back
              to you within 24-48 hours. Our support team will respond to you at{' '}
              <strong>{formData.email}</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Return Home
              </Link>
              <button
                onClick={() => {
                  setFormSubmitted(false);
                  setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    subject: '',
                    category: 'general',
                    message: '',
                    agreeToPrivacy: false,
                  });
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Send Another Message
              </button>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-teal-400 to-green-400 bg-clip-text text-transparent">
            Contact Us
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Have questions, feedback, or need support? We'd love to hear from you. Our team is here
            to help make your ChillFree TV experience amazing.
          </p>
        </div>

        {/* Contact Options */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📧</span>
            </div>
            <h3 className="text-lg font-semibold mb-2 text-teal-400">Email Support</h3>
            <p className="text-gray-300 text-sm mb-3">
              Get help with technical issues, account questions, or general support.
            </p>
            <a
              href="mailto:support@chillfree.tv"
              className="text-teal-400 hover:text-teal-300 underline font-semibold"
            >
              support@chillfree.tv
            </a>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
            <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🔒</span>
            </div>
            <h3 className="text-lg font-semibold mb-2 text-purple-400">Privacy Requests</h3>
            <p className="text-gray-300 text-sm mb-3">
              Exercise your privacy rights or submit data-related requests.
            </p>
            <a
              href="mailto:datarequest@chillfree.tv"
              className="text-purple-400 hover:text-purple-300 underline font-semibold"
            >
              datarequest@chillfree.tv
            </a>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⏱️</span>
            </div>
            <h3 className="text-lg font-semibold mb-2 text-green-400">Response Time</h3>
            <p className="text-gray-300 text-sm mb-3">
              We typically respond to all inquiries within 24-48 hours.
            </p>
            <span className="text-green-400 font-semibold">24-48 Hours</span>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
          <h2 className="text-2xl font-bold mb-6 text-teal-400">Send Us a Message</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Fields */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Enter your first name"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Enter your last name"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Enter your email address"
              />
            </div>

            {/* Category and Subject */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  id="category"
                  name="category"
                  required
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="general">General Inquiry</option>
                  <option value="technical">Technical Support</option>
                  <option value="content">Content Request</option>
                  <option value="privacy">Privacy/Data Request</option>
                  <option value="business">Business/Partnership</option>
                  <option value="feedback">Feedback/Suggestion</option>
                  <option value="bug">Bug Report</option>
                </select>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Brief description of your inquiry"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={6}
                value={formData.message}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-vertical"
                placeholder="Please provide details about your inquiry, including any relevant information that might help us assist you better..."
              />
              <p className="text-gray-400 text-sm mt-1">
                Minimum 10 characters. Be as detailed as possible to help us provide the best
                assistance.
              </p>
            </div>

            {/* Privacy Agreement */}
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="agreeToPrivacy"
                  name="agreeToPrivacy"
                  required
                  checked={formData.agreeToPrivacy}
                  onChange={handleInputChange}
                  className="mt-1 w-4 h-4 text-teal-600 bg-gray-600 border-gray-500 rounded focus:ring-teal-500 focus:ring-2"
                />
                <label htmlFor="agreeToPrivacy" className="text-sm text-gray-300">
                  I agree to the{' '}
                  <button
                    onClick={() => window.open('/privacy-policy', '_blank')}
                    className="text-teal-400 hover:text-teal-300 underline bg-transparent border-none cursor-pointer"
                  >
                    Privacy Policy
                  </button>{' '}
                  and{' '}
                  <button onClick={() => window.open('/terms-of-use', '_blank')} className="text-teal-400 hover:text-teal-300 underline bg-transparent border-none cursor-pointer">
                    Terms of Use
                  </button>
                  . I understand that my information will be used to respond to my inquiry and may
                  be stored for customer service purposes.
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full px-6 py-4 rounded-lg font-semibold text-lg transition-all duration-300 ${
                isSubmitting
                  ? 'bg-gray-600 cursor-not-allowed'
                  : 'bg-gradient-to-r from-teal-600 to-green-600 hover:from-teal-700 hover:to-green-700 transform hover:scale-105'
              } text-white`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Sending Message...</span>
                </div>
              ) : (
                'Send Message'
              )}
            </button>
          </form>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-8 text-center text-teal-400">
            Frequently Asked Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold mb-3 text-green-400">
                How do I report a technical issue?
              </h3>
              <p className="text-gray-300 text-sm">
                Select "Technical Support" as your category and provide details about the issue,
                including your device, browser, and steps to reproduce the problem.
              </p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold mb-3 text-green-400">
                Can I request specific content?
              </h3>
              <p className="text-gray-300 text-sm">
                Yes! Use the "Content Request" category to suggest movies or TV shows you'd like to
                see on ChillFree TV. We review all suggestions.
              </p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold mb-3 text-green-400">
                How do I exercise my privacy rights?
              </h3>
              <p className="text-gray-300 text-sm">
                For privacy-related requests, use our dedicated email{' '}
                <a href="mailto:datarequest@chillfree.tv" className="text-teal-400 underline">
                  datarequest@chillfree.tv
                </a>{' '}
                or visit our{' '}
                <Link to="/ccpa" className="text-teal-400 underline">
                  CCPA page
                </Link>
                .
              </p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold mb-3 text-green-400">
                What's your response time?
              </h3>
              <p className="text-gray-300 text-sm">
                We aim to respond to all inquiries within 24-48 hours. Complex technical issues may
                take longer, but we'll keep you updated on our progress.
              </p>
            </div>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="mt-12 text-center">
          <h3 className="text-xl font-semibold mb-6 text-teal-400">Additional Resources</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/about"
              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              About Us
            </Link>
            <button
              onClick={() => window.open('/privacy-policy', '_blank')}
              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors border-none cursor-pointer"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => window.open('/terms-of-use', '_blank')}
              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors border-none cursor-pointer"
            >
              Terms of Use
            </button>
            <Link
              to="/ccpa"
              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              CCPA Rights
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
